/**
 * ReadyToAnalyze — connected, no data yet. Replaces the empty white canvas.
 */

import React from 'react';
import { Button, Typography } from 'antd';
import { motion, useReducedMotion } from 'framer-motion';
import { MailCheck, BarChart2, Clock, Users, LayoutGrid, Search } from 'lucide-react';
import { STAGGER_CONTAINER, FADE_UP } from '../constants';

const { Title, Text } = Typography;

const PREVIEWS = [
  { icon: BarChart2, label: 'Volume', hint: 'Sent vs received', color: '#6366f1' },
  { icon: Clock, label: 'Speed', hint: 'Reply times & delays', color: '#10b981' },
  { icon: Users, label: 'Contacts', hint: 'Who you write most', color: '#8b5cf6' },
  { icon: LayoutGrid, label: 'Heatmap', hint: 'Busiest hours', color: '#f59e0b' },
];

export function ReadyToAnalyze({ isDark, onAnalyze, rangeDays = 7 }) {
  const reduced = useReducedMotion();
  const container = reduced ? {} : STAGGER_CONTAINER;
  const item = reduced ? {} : FADE_UP;
  const ink = isDark ? '#f8fafc' : '#0f172a';
  const mute = isDark ? '#94a3b8' : '#64748b';
  const card = isDark ? 'rgba(255,255,255,0.035)' : '#ffffff';
  const line = isDark ? 'rgba(255,255,255,0.07)' : '#eceef3';
  const tile = isDark ? 'rgba(255,255,255,0.04)' : '#f8f7ff';

  return (
    <div style={{ display: 'flex', justifyContent: 'center', padding: '8px 4px 20px' }}>
      <motion.div
        variants={container}
        initial="hidden"
        animate="visible"
        style={{
          width: '100%',
          maxWidth: 760,
          background: card,
          border: `1px solid ${line}`,
          borderRadius: 22,
          padding: '36px 32px 28px',
          textAlign: 'center',
          position: 'relative',
          overflow: 'hidden',
          boxShadow: isDark
            ? '0 16px 44px rgba(0,0,0,0.26)'
            : '0 14px 40px rgba(99,102,241,0.07)',
        }}
      >
        <div
          aria-hidden
          style={{
            position: 'absolute',
            inset: '-50% -10% auto',
            height: 220,
            background: isDark
              ? 'radial-gradient(ellipse at 50% 0%, rgba(16,185,129,0.14), transparent 60%)'
              : 'radial-gradient(ellipse at 50% 0%, rgba(16,185,129,0.1), transparent 60%)',
            pointerEvents: 'none',
          }}
        />

        <motion.div variants={item} style={{ position: 'relative', marginBottom: 22 }}>
          <div style={{ position: 'relative', width: 76, height: 76, margin: '0 auto 16px' }}>
            {!reduced && (
              <motion.div
                animate={{ scale: [1, 1.22, 1], opacity: [0.4, 0, 0.4] }}
                transition={{ duration: 2.2, repeat: Infinity, ease: 'easeInOut' }}
                style={{
                  position: 'absolute', inset: -4, borderRadius: 22,
                  border: '1.5px solid rgba(16,185,129,0.4)',
                }}
              />
            )}
            <div style={{
              width: 76, height: 76, borderRadius: 22,
              background: 'linear-gradient(145deg, #10b981 0%, #059669 100%)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: '0 10px 24px rgba(16,185,129,0.32)',
            }}>
              <MailCheck size={32} color="#fff" strokeWidth={1.8} />
            </div>
          </div>

          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            padding: '4px 10px', borderRadius: 99, marginBottom: 12,
            background: isDark ? 'rgba(16,185,129,0.12)' : 'rgba(16,185,129,0.1)',
            color: '#059669', fontSize: 12, fontWeight: 650,
          }}>
            Gmail connected
          </div>

          <Title level={4} style={{ margin: '0 0 8px', fontWeight: 750, letterSpacing: '-0.3px', color: ink }}>
            Click Analyze to see your patterns
          </Title>
          <Text style={{ fontSize: 14, lineHeight: 1.6, color: mute, maxWidth: 420, display: 'block', margin: '0 auto' }}>
            Last 7 days is pre-selected. Change the range above if you want, then start analysis.
          </Text>

          {onAnalyze && (
            <div style={{ marginTop: 18, display: 'flex', justifyContent: 'center' }}>
              <motion.div
                animate={reduced ? {} : { boxShadow: ['0 8px 20px rgba(99,102,241,0.28)', '0 8px 28px rgba(99,102,241,0.5)', '0 8px 20px rgba(99,102,241,0.28)'] }}
                transition={{ duration: 1.8, repeat: Infinity }}
                style={{ borderRadius: 12 }}
              >
                <Button
                  type="primary"
                  size="large"
                  icon={<Search size={16} />}
                  onClick={onAnalyze}
                  style={{
                    height: 48,
                    padding: '0 28px',
                    borderRadius: 12,
                    fontWeight: 750,
                    fontSize: 15.5,
                    background: 'linear-gradient(135deg, #6366f1 0%, #7c3aed 100%)',
                    border: 'none',
                  }}
                >
                  Analyze last {rangeDays} days
                </Button>
              </motion.div>
            </div>
          )}
        </motion.div>

        <motion.div
          variants={item}
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
            gap: 10,
          }}
        >
          {PREVIEWS.map(({ icon: Icon, label, hint, color }, i) => (
            <motion.div
              key={label}
              whileHover={reduced ? {} : { y: -3 }}
              transition={{ duration: 0.2 }}
              style={{
                padding: '14px 10px 12px',
                background: tile,
                border: `1px solid ${line}`,
                borderRadius: 14,
                textAlign: 'left',
              }}
            >
              <div style={{
                width: 30, height: 30, borderRadius: 9, marginBottom: 10,
                background: `${color}18`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <Icon size={15} color={color} strokeWidth={2.1} />
              </div>
              <Text strong style={{ display: 'block', fontSize: 12.5, color: ink }}>{label}</Text>
              <Text style={{ fontSize: 11, color: mute }}>{hint}</Text>
              {!reduced && (
                <motion.div
                  animate={{ opacity: [0.35, 0.7, 0.35] }}
                  transition={{ duration: 1.8, repeat: Infinity, delay: i * 0.15 }}
                  style={{
                    marginTop: 10, height: 4, borderRadius: 99,
                    background: `linear-gradient(90deg, ${color}55, ${color}12)`,
                  }}
                />
              )}
            </motion.div>
          ))}
        </motion.div>
      </motion.div>
    </div>
  );
}
